import type { MessageKind as EngineMessageKind } from "@kaleidoscope-solutions/engine-wasm"

// Default the starting enum index as 90000 to avoid index conflicts with MessageKind
// enum from the engine.
export enum UIMessageKind {
  ContextUpdated = 90000,
  DataImported,
  Undo,
  Redo,
  CellValueSet,
  OverlayClosed,
  EntityDeleted,
}

type Listener = (content: any) => void

class MessageHandler<MessageKind> {
  // Store a map of messages by MessageKind
  messages: Map<MessageKind, Set<Listener>>

  constructor() {
    this.messages = new Map<MessageKind, Set<Listener>>()
  }

  // Map of callback message listeners
  on(messageKind: MessageKind, listener: Listener) {
    if (typeof listener !== "function") {
      throw new TypeError("The listener must be a function")
    }

    let listeners = this.messages.get(messageKind)
    if (!listeners) {
      listeners = new Set()
      this.messages.set(messageKind, listeners)
    }
    listeners.add(listener)
    return this
  }

  // Remove listener
  off(messageKind: MessageKind, listener: Listener): void {
    const listeners = this.messages.get(messageKind)
    if (listeners) {
      listeners.delete(listener) // Remove callback from the set
      if (listeners.size === 0) {
        this.messages.delete(messageKind) // Clean up if no listeners remain
      }
    }
  }

  // Triggering the callbacks
  emit(messageKind: MessageKind, ...args: [content: any]) {
    const listeners = this.messages.get(messageKind)
    if (listeners) {
      for (const listener of listeners) {
        listener.apply(this, args)
      }
    }
    return this
  }
}

const messageHandler = new MessageHandler<EngineMessageKind | UIMessageKind>()

export default messageHandler
